import { useEffect } from "react";
import { useAuth } from "@/providers/auth-provider";
import { apiRequest } from "@/services/api-client";

/**
 * Collects a browser fingerprint via FingerprintJS and POSTs the hash to the
 * server once per session (guarded by sessionStorage). Fire-and-forget.
 */
export const useFingerprint = () => {
  const { state } = useAuth();

  useEffect(() => {
    if (state.status !== "authenticated") return;
    if (sessionStorage.getItem("fp_sent")) return;

    let cancelled = false;

    const run = async () => {
      try {
        const FingerprintJS = await import("@fingerprintjs/fingerprintjs");
        const fp = await FingerprintJS.load();
        const { visitorId } = await fp.get();

        // SHA-256 hash in browser (SubtleCrypto)
        const encoder = new TextEncoder();
        const data = encoder.encode(visitorId);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const fingerprintHash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

        if (cancelled) return;

        await apiRequest("/player/fingerprint", {
          method: "POST",
          body: JSON.stringify({ fingerprintHash }),
        });

        sessionStorage.setItem("fp_sent", "1");
      } catch {
        // Fire-and-forget — failure is silent
      }
    };

    run();
    return () => { cancelled = true; };
  }, [state.status]);
};
